import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { getApiConfig } from "../utils/config";

let _tokenFetcher: () => Promise<string>;

// Define authentication-related endpoints that should use the public API
const AUTH_ENDPOINTS = [
  '/auth',
  '/oauth',
  '/login',
  '/logout',
  '/token',
  '/userinfo',
  '/.well-known',
  '/user',
  '/profile'
];

// Define public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/health',
  '/status',
  '/ping'
];

/**
 * Check if a URL matches a specific endpoint using proper path-based matching
 * @param url The URL to check
 * @param endpoint The endpoint pattern to match against
 * @returns true if the URL starts with the endpoint and is followed by a path boundary
 */
function matchesEndpoint(url: string, endpoint: string): boolean {
  // Remove leading slash from URL for consistent comparison
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;

  // Check if URL starts with the endpoint
  if (!cleanUrl.startsWith(endpoint)) {
    return false;
  }

  // If the endpoint is exactly the URL, it's a match
  if (cleanUrl === endpoint) {
    return true;
  }

  // Check if the next character after the endpoint is a path separator
  // This ensures we don't match partial paths (e.g., '/auth' shouldn't match '/authenticate')
  const nextChar = cleanUrl.charAt(endpoint.length);
  return nextChar === '/' || nextChar === '?' || nextChar === '#';
}

/**
 * Ensure a relative request path is root-relative.
 *
 * Some service modules declare their root without a leading slash (e.g.
 * "notification-manager"). That is harmless while baseURL is an absolute URL,
 * because axios joins the two and strips any leading slash while doing so. But
 * when baseURL is empty — the same-origin setup private deployments use, where
 * nginx proxies the API — axios treats the empty baseURL as falsy and passes
 * the path through untouched. The browser then resolves it against the current
 * page path, so a call from /ml-deployments would go to
 * /ml-deployments/notification-manager/... and miss the proxy entirely.
 *
 * Absolute URLs and protocol-relative URLs are left alone.
 */
function toRootRelative(url: string): string {
  if (!url || url.startsWith("//") || /^[a-z][a-z\d+\-.]*:/i.test(url)) {
    return url;
  }

  return url.startsWith("/") ? url : `/${url}`;
}

function getBaseUrlForRequest(url: string): string {
  const apiConfig = getApiConfig();

  // Check if this is an authentication-related request
  const isAuthRequest = AUTH_ENDPOINTS.some(endpoint =>
    matchesEndpoint(url, endpoint)
  );

  // Check if this is a public endpoint
  const isPublicRequest = PUBLIC_ENDPOINTS.some(endpoint =>
    matchesEndpoint(url, endpoint)
  );

  let selectedUrl: string;

  if (isAuthRequest) {
    selectedUrl = apiConfig.authApiUrl;
  } else if (isPublicRequest) {
    selectedUrl = apiConfig.publicApiUrl;
  } else {
    selectedUrl = apiConfig.privateApiUrl;
  }

  // Debug logging
  if (process.env.REACT_APP_DEBUG_API_ROUTING === 'true') {
    console.log(`API Routing Debug:`, {
      url,
      isAuthRequest,
      isPublicRequest,
      selectedUrl,
      authApiUrl: apiConfig.authApiUrl,
      publicApiUrl: apiConfig.publicApiUrl,
      privateApiUrl: apiConfig.privateApiUrl
    });
  }

  return selectedUrl;
}

const createApiService = () => {
  const apiService = axios.create({
    headers: {
      "Content-Type": "application/json",
    },
  });

  apiService.interceptors.request.use(
    async (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      config.headers = config.headers || {};

      // Normalise before routing so a slash-less service root cannot be
      // resolved against the current page path when baseURL is empty.
      config.url = toRootRelative(config.url || '');

      // Set the base URL based on the request type
      const baseUrl = getBaseUrlForRequest(config.url);
      config.baseURL = baseUrl;

      if (!_tokenFetcher) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel(
              "Axios API was not initialized with the appropriate token fetching function."
            )
          ),
        } as unknown as InternalAxiosRequestConfig);
      }

      try {
        const jwt = await _tokenFetcher();
        return Promise.resolve({
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${jwt}` },
        } as unknown as InternalAxiosRequestConfig);
      } catch (e) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel("No access token!")
          ),
        } as unknown as InternalAxiosRequestConfig);
      }
    },
    (error: Error) => Promise.reject(error)
  );

  apiService.interceptors.response.use(
    (response: any) => {
      return response;
    },
    (error: any) => {
      return Promise.reject(error.response?.data || error.response);
    }
  );

  return apiService;
};

const apiService = createApiService();

export default apiService;

/**
 * Provide the ability to pass the getAccessTokenSilently function from Auth0 when the app bootstraps.
 * @param tokenFetcher
 */
export const setTokenFetcher = (tokenFetcher: () => Promise<string>) => {
  _tokenFetcher = tokenFetcher;
  return this;
};
